import React, { useState } from 'react';
import { OrderApi, PayoutBankDetails } from '@/lib/OrderApi';
import { MainButton } from '@/components/shared/MainButton';
import { RiBankLine, RiCheckLine, RiPencilLine } from '@remixicon/react';
import { isValidIban, maskIban } from '@/lib/iban';
import * as m from "@/paraglide/messages.js"

interface PayoutBankDetailsCardProps {
    orderId: string;
    requiresPayoutBankDetails: boolean;
    reason?: 'sale' | 'failed_refund';
    payoutBankDetails: PayoutBankDetails | null;
    onSuccess: () => void;
}

const inputClassName = "mt-1 block w-full rounded-md border-0 py-2 px-3 shadow-sm ring-1 ring-inset ring-[var(--shop-borderColor)] placeholder:text-[var(--shop-textColor)] placeholder:opacity-40 focus:ring-2 focus:ring-inset focus:ring-[var(--shop-ctaButtonColor)] sm:text-sm bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)]";
const labelClassName = "block text-sm font-medium text-[var(--shop-textColor)]";
const errorClassName = "mt-2 text-sm text-red-600";

const PayoutBankDetailsCard: React.FC<PayoutBankDetailsCardProps> = ({ orderId, requiresPayoutBankDetails, reason, payoutBankDetails, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const [isEditing, setIsEditing] = useState(false);
    const [accountHolder, setAccountHolder] = useState(payoutBankDetails?.accountHolder ?? '');
    const [iban, setIban] = useState(payoutBankDetails?.iban ?? '');

    const showForm = isEditing || (requiresPayoutBankDetails && !payoutBankDetails);

    const clearError = (field: string) => {
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string[]> = {};

        if (!accountHolder || accountHolder.trim().length < 2) {
            newErrors.account_holder_name = [m.order_payout_bank_details_account_holder_required()];
        }
        if (!isValidIban(iban)) {
            newErrors.iban = [m.order_payout_bank_details_iban_invalid()];
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setErrors({});

        try {
            const orderApi = new OrderApi(import.meta.env.VITE_API_URL);
            const result = await orderApi.submitPayoutBankDetails(orderId, {
                account_holder_name: accountHolder.trim(),
                iban: iban,
            });

            if (!result.success) {
                setErrors(result.errors);
                return;
            }

            setIsEditing(false);
            // Re-fetch the order to get the updated requiresPayoutBankDetails / payoutBankDetails state.
            onSuccess();
        } catch (error) {
            console.error('Failed to save bank details:', error);
            setErrors({ general: [m.order_payout_bank_details_error_general()] });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] rounded-md px-4 sm:px-8 py-4'>
            <h3 className='text-lg font-semibold mb-3 flex items-center gap-2'>
                <RiBankLine className='w-5 h-5' />
                {showForm ? m.order_payout_bank_details_title() : m.order_payout_bank_details_confirmation_title()}
            </h3>

            {showForm ? (
                <div className='space-y-4'>
                    <p className='text-sm text-[var(--shop-textColor)]/90'>
                        {reason === 'failed_refund' ? m.order_payout_bank_details_description_refund() : m.order_payout_bank_details_description()}
                    </p>

                    {errors.general && (
                        <div className="p-3 rounded-md bg-[var(--shop-productErrorMessageBackground)] text-[var(--shop-productErrorMessageText)]">
                            {errors.general.map((error, i) => (
                                <p key={i}>{error}</p>
                            ))}
                        </div>
                    )}

                    <div>
                        <label htmlFor="account_holder_name" className={labelClassName}>
                            {m.order_payout_bank_details_account_holder()} <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="account_holder_name"
                            value={accountHolder}
                            onChange={(e) => { setAccountHolder(e.target.value); clearError('account_holder_name'); }}
                            className={inputClassName}
                            placeholder={m.order_payout_bank_details_account_holder_placeholder()}
                        />
                        {errors.account_holder_name && <p className={errorClassName}>{errors.account_holder_name[0]}</p>}
                    </div>

                    <div>
                        <label htmlFor="iban" className={labelClassName}>
                            {m.order_payout_bank_details_iban()} <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="iban"
                            value={iban}
                            onChange={(e) => { setIban(e.target.value); clearError('iban'); }}
                            className={inputClassName}
                            placeholder={m.order_payout_bank_details_iban_placeholder()}
                            autoCapitalize="characters"
                            autoCorrect="off"
                            spellCheck={false}
                        />
                        {errors.iban && <p className={errorClassName}>{errors.iban[0]}</p>}
                    </div>

                    <MainButton
                        onClick={handleSubmit}
                        disabled={loading}
                        loading={loading}
                        className="w-full mt-2"
                    >
                        <div className="flex items-center justify-center gap-2">
                            {loading ? m.order_payout_bank_details_submitting() : m.order_payout_bank_details_submit()}
                            <RiBankLine className="w-5 h-5" />
                        </div>
                    </MainButton>
                </div>
            ) : (
                <div className='space-y-3'>
                    <div className='flex items-start gap-2 text-sm text-[var(--shop-textColor)]/90'>
                        <RiCheckLine className='w-5 h-5 flex-shrink-0 text-green-600' />
                        <p>{m.order_payout_bank_details_confirmation({ iban: maskIban(payoutBankDetails!.iban) })}</p>
                    </div>
                    <button
                        onClick={() => { setIsEditing(true); setErrors({}); }}
                        className='flex items-center gap-1 text-sm text-[var(--shop-ctaButtonColor)] hover:opacity-80 transition-opacity'
                    >
                        <RiPencilLine className='w-4 h-4' />
                        {m.order_payout_bank_details_edit()}
                    </button>
                </div>
            )}
        </div>
    );
};

export default PayoutBankDetailsCard;
